import { z } from "zod";
import { OpaqueIdSchema } from "./schemas";
import { CommercialTermSchema } from "./commercial-terms";
import { COMMERCIAL_TERMS_URL } from "./commercial-document-links";

export const COMMERCIAL_DOCUMENT_LABELS = {
  quote: "Quote",
  order_form: "Order form",
  change_order: "Change order",
} as const;
export const CommercialDocumentKindSchema = z.enum(["quote", "order_form", "change_order"]);
const text = (max: number) => z.string().trim().max(max);
export const CommercialDocumentInputSchema = z
  .strictObject({
    kind: CommercialDocumentKindSchema,
    title: text(200).min(1),
    customerName: text(160).min(1),
    toolName: text(200),
    reference: text(80).min(1),
    documentDate: z.iso.date(),
    expiresOn: z.iso.date().nullable(),
    contactName: text(160),
    contactEmail: z.union([z.literal(""), z.email().max(254)]),
    initialTerm: text(300),
    originalReference: text(200),
    commercialTerms: z.array(CommercialTermSchema).min(1).max(40),
    scope: text(20000).min(1),
    scopeControls: text(4000),
    timelineImpact: text(4000),
    governingTerms: text(8000),
    includeAcceptance: z.boolean(),
  })
  .refine((v) => !v.expiresOn || v.expiresOn >= v.documentDate, {
    message: "Expiration must be on or after the document date.",
    path: ["expiresOn"],
  })
  .refine((v) => v.kind !== "change_order" || v.originalReference.length > 0, {
    message: "Enter the original order reference.",
    path: ["originalReference"],
  })
  .refine(
    (v) => new Set(v.commercialTerms.map((row) => row.id)).size === v.commercialTerms.length,
    {
      message: "Commercial term identifiers must be unique.",
      path: ["commercialTerms"],
    },
  )
  .refine((v) => new TextEncoder().encode(JSON.stringify(v)).length <= 80000, {
    message: "This document is too large. Shorten the content before saving.",
  });
export const CommercialRevisionSummarySchema = z.strictObject({
  id: OpaqueIdSchema,
  revision: z.number().int().positive(),
  title: text(200).min(1),
  kind: CommercialDocumentKindSchema,
  reference: text(80).min(1),
  author: text(160).min(1),
  createdAt: z.number().int().positive(),
  changeNote: text(500),
  restoredFrom: z.number().int().positive().nullable(),
});
export const CommercialDocumentSchema = CommercialRevisionSummarySchema.extend({
  document: CommercialDocumentInputSchema,
});
export const CommercialDocumentFilingSchema = z.strictObject({
  revision: z.number().int().positive(),
  customerDocumentId: OpaqueIdSchema,
  signedOn: z.iso.date(),
  filedBy: text(160).min(1),
  filedAt: z.number().int().positive(),
});
export const CommercialDocumentResponseSchema = z.strictObject({
  document: CommercialDocumentSchema,
  // Omitted by older Workers; absent and null both mean the document is not filed.
  filing: CommercialDocumentFilingSchema.nullable().optional(),
});
export const CommercialDocumentListSchema = z.strictObject({
  documents: z
    .array(
      CommercialRevisionSummarySchema.extend({
        filing: CommercialDocumentFilingSchema.nullable().optional(),
      }),
    )
    .max(50),
  nextCursor: OpaqueIdSchema.nullable(),
});
export const CommercialDocumentFileSignedSchema = z.strictObject({
  revision: z.number().int().positive(),
  fileId: OpaqueIdSchema,
  signedOn: z.iso.date(),
});
export const CommercialDocumentHistorySchema = z.strictObject({
  revisions: z.array(CommercialRevisionSummarySchema).max(50),
  nextBefore: z.number().int().positive().nullable(),
});
export const CommercialDocumentSaveSchema = z.strictObject({
  document: CommercialDocumentInputSchema,
  expectedRevision: z.number().int().nonnegative(),
  changeNote: text(500),
});
export const CommercialDocumentRestoreSchema = z.strictObject({
  expectedRevision: z.number().int().positive(),
  revision: z.number().int().positive(),
});
export type CommercialDocumentInput = z.infer<typeof CommercialDocumentInputSchema>;
export type CommercialDocument = z.infer<typeof CommercialDocumentSchema>;
export type CommercialDocumentExport = {
  document: CommercialDocumentInput;
  revision: number | null;
};
export type CommercialRevisionSummary = z.infer<typeof CommercialRevisionSummarySchema>;
export type CommercialDocumentFiling = z.infer<typeof CommercialDocumentFilingSchema>;

/** Where a signed copy lands in the agreement register: quotes as accepted quotes, the rest as agreements. */
export function commercialFilingRegister(kind: CommercialDocumentInput["kind"]) {
  return kind === "quote"
    ? ({ kind: "quote", status: "accepted" } as const)
    : ({ kind: "agreement", status: "signed" } as const);
}

/** A new draft carrying another document's content, for the next order or a revision. */
export function duplicateCommercialDocument(
  source: CommercialDocumentInput,
  today = new Date().toISOString().slice(0, 10),
): CommercialDocumentInput {
  return {
    ...source,
    title: `${source.title} (copy)`.slice(0, 200),
    reference: "",
    documentDate: today,
    expiresOn: null,
    commercialTerms: source.commercialTerms.map((row) => ({ ...row })),
  };
}

export function standardCommercialGoverningTerms(kind: CommercialDocumentInput["kind"]) {
  const name = { quote: "Quote", order_form: "Order Form", change_order: "Change Order" }[kind];
  return `This ${name} is governed by the Delta Adaptive Systems Standard Customer Terms available at ${COMMERCIAL_TERMS_URL} that are in effect on the date this ${name} is executed, and those terms are incorporated by reference. Customer acknowledges that it has had the opportunity to review and agrees to those terms. Subsequent revisions do not modify this ${name} unless Company and Customer agree otherwise in writing.`;
}

export function newCommercialDocument(
  kind: CommercialDocumentInput["kind"],
  customerName: string,
): CommercialDocumentInput {
  return {
    kind,
    customerName,
    title: `${COMMERCIAL_DOCUMENT_LABELS[kind]} - ${customerName}`,
    reference: "",
    documentDate: new Date().toISOString().slice(0, 10),
    expiresOn: null,
    toolName: "",
    contactName: "",
    contactEmail: "",
    initialTerm: "",
    originalReference: "",
    commercialTerms: [
      { id: "term_initial", label: "Development fee", value: "", category: "fixed" },
    ],
    scope: "",
    scopeControls: "",
    timelineImpact: "",
    governingTerms: standardCommercialGoverningTerms(kind),
    includeAcceptance: true,
  };
}
